"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import Image from "next/image";
import { content } from "@/config/content";

// Phase 3.1 — Hero slides CMS-driven. Fallback kept for empty DB / initial load
// flash before the Client hook resolves.
// Phase B7：data 改由 page.js (Server Component) 預先 fetch 並 pass 為 props，
// 移除原 useSiteContent / useSiteSettings 三個 client-side fetch（防 FOUC + 雙渲染）。
const FALLBACK_SLIDES = [
  {
    id: "fb-1",
    imageUrl: "/hero/v2/works-collage.jpg",
    imageUrlMobile: "/hero/v2-mobile/works-collage.jpg",
    altText: "",
  },
  {
    id: "fb-2",
    imageUrl: "/hero/v2/design-grid.jpg",
    imageUrlMobile: "/hero/v2-mobile/design-grid.jpg",
    altText: "",
  },
  {
    id: "fb-3",
    imageUrl: "/hero/v2/catch-grid.jpg",
    imageUrlMobile: "/hero/v2-mobile/catch-grid.jpg",
    altText: "",
  },
  {
    id: "fb-4",
    imageUrl: "/hero/v2/zhuyin.jpg",
    imageUrlMobile: "/hero/v2-mobile/zhuyin.jpg",
    altText: "",
  },
  {
    id: "fb-5",
    imageUrl: "/hero/v2/logo-cat.jpg",
    imageUrlMobile: "/hero/v2-mobile/logo-cat.jpg",
    altText: "",
  },
  {
    id: "fb-6",
    imageUrl: "/hero/v2/catch-logo.jpg",
    imageUrlMobile: "/hero/v2-mobile/catch-logo.jpg",
    altText: "",
  },
  {
    id: "fb-7",
    imageUrl: "/hero/v2/catch-row.jpg",
    imageUrlMobile: "/hero/v2-mobile/catch-row.jpg",
    altText: "",
  },
];

const SWIPE_THRESHOLD = 50;
const AUTO_INTERVAL = 5000;

export default function Hero({
  initialHeroItems = [],
  initialSlides = [],
  initialBrand = {},
}) {
  // Phase B7：所有 data 來自 server-side fetch (page.js)，client 不再 useSiteContent / useSiteSettings
  const heroRow = initialHeroItems[0];
  const heroTitle = heroRow?.title || content.hero.title;
  const heroSubtitle = heroRow?.body || content.hero.subtitle;

  const slides = initialSlides.length > 0 ? initialSlides : FALLBACK_SLIDES;

  const brand = initialBrand;
  const brandName = brand.brand_name || content.brand.name;
  const brandNameEn = brand.brand_name_en || content.brand.nameEn;
  const brandLocationIntl =
    brand.brand_location_intl || content.brand.locationIntl;

  const [current, setCurrent] = useState(0);
  const total = slides.length;
  const touchRef = useRef({ startX: 0, startY: 0 });
  const timerRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(
      () => setCurrent((p) => (p + 1) % total),
      AUTO_INTERVAL,
    );
  }, [total]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const goTo = useCallback(
    (i) => {
      setCurrent(i);
      resetTimer();
    },
    [resetTimer],
  );

  const goNext = useCallback(
    () => goTo((current + 1) % total),
    [current, goTo, total],
  );
  const goPrev = useCallback(
    () => goTo((current - 1 + total) % total),
    [current, goTo, total],
  );

  const onTouchStart = useCallback((e) => {
    touchRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
    };
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      const dx = e.changedTouches[0].clientX - touchRef.current.startX;
      const dy = e.changedTouches[0].clientY - touchRef.current.startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD) {
        dx < 0 ? goNext() : goPrev();
      }
    },
    [goNext, goPrev],
  );

  return (
    <section
      className="relative flex flex-col items-center w-full pt-6 md:pt-10"
      style={{ backgroundColor: "var(--color-bg)" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="relative w-full aspect-[4/5] md:aspect-[16/9] overflow-hidden mx-auto"
        style={{
          maxWidth:
            "min(1680px, calc((100dvh - var(--navbar-h) - 220px) * 16 / 9))",
        }}
      >
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <Image
              src={slide.imageUrl}
              alt={slide.altText || ""}
              fill
              priority={i === 0}
              fetchPriority={i === 0 ? "high" : "auto"}
              loading={i === 0 ? "eager" : "lazy"}
              sizes="(max-width: 768px) 100vw, (max-width: 1680px) 100vw, 1680px"
              className="object-cover"
              quality={75}
            />
          </div>
        ))}

        <button
          onClick={goPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 hidden sm:flex items-center justify-center transition-opacity hover:opacity-100"
          style={{
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: "50%",
            opacity: 0.5,
          }}
          aria-label="上一張"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          onClick={goNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 hidden sm:flex items-center justify-center transition-opacity hover:opacity-100"
          style={{
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: "50%",
            opacity: 0.5,
          }}
          aria-label="下一張"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className="absolute bottom-4 left-0 right-0 z-20 flex gap-1 justify-center">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label={`切換到第 ${i + 1} 張`}
            >
              <span
                className="block transition-all"
                style={{
                  width: i === current ? "24px" : "8px",
                  height: "8px",
                  borderRadius: "4px",
                  backgroundColor:
                    i === current ? "var(--color-text)" : "rgba(0,0,0,0.25)",
                }}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="relative w-full text-center px-6 py-6 md:py-8">
        <p
          className="tracking-[0.3em] text-xs mb-4 uppercase"
          style={{
            color: "var(--color-textMuted)",
            fontFamily: "var(--font-accent)",
          }}
        >
          {brandLocationIntl}
        </p>
        <h1
          className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight"
          style={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-text)",
          }}
        >
          {brandName}
        </h1>
        <p
          className="text-lg md:text-xl tracking-[0.15em] mb-8"
          style={{
            fontFamily: "var(--font-accent)",
            color: "var(--color-textMuted)",
          }}
        >
          {brandNameEn}
        </p>
        <p
          className="text-base md:text-lg max-w-md mb-3 leading-relaxed mx-auto"
          style={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-text)",
          }}
        >
          {heroTitle}
        </p>
        <p className="text-sm" style={{ color: "var(--color-textSecondary)" }}>
          {heroSubtitle}
        </p>
      </div>
    </section>
  );
}
